import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Link } from "react-router-dom";

export default function ComplianceAmendments() {
  const [items, setItems] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [f, setF] = useState({ section_number: "", section_title: "", change_summary: "", is_material: false });

  const load = async () => {
    const { data } = await supabase.from("compliance_policy_amendments").select("*").order("amended_at", { ascending: false });
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("compliance_policy_amendments").insert({
      section_number: Number(f.section_number),
      section_title: f.section_title || null,
      change_summary: f.change_summary,
      is_material: f.is_material,
      amended_by: u.user?.id,
    });
    if (error) toast({ title: "Failed", description: error.message, variant: "destructive" });
    else { setOpen(false); load(); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Policy Amendment Log (§18)</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild><Link to="/admin/tier1-policy-editor">Open Editor</Link></Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm">Log amendment</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log policy amendment</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Section #</Label><Input type="number" value={f.section_number} onChange={(e) => setF({ ...f, section_number: e.target.value })} /></div>
                  <div><Label>Section title</Label><Input value={f.section_title} onChange={(e) => setF({ ...f, section_title: e.target.value })} /></div>
                </div>
                <div><Label>Change summary</Label><Textarea value={f.change_summary} onChange={(e) => setF({ ...f, change_summary: e.target.value })} /></div>
                <div className="flex items-center gap-2"><Switch checked={f.is_material} onCheckedChange={(v) => setF({ ...f, is_material: v })} /><Label>Material change (requires Board approval per §18)</Label></div>
                <Button onClick={save} disabled={!f.section_number || !f.change_summary}>Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? <p className="text-muted-foreground text-sm">No amendments logged.</p> :
          <table className="w-full text-sm">
            <thead className="border-b"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Section</th><th className="text-left p-2">Change</th><th className="text-left p-2">Materiality</th></tr></thead>
            <tbody>{items.map((it) => (
              <tr key={it.id} className="border-b">
                <td className="p-2">{format(new Date(it.amended_at), "dd/MM/yyyy")}</td>
                <td className="p-2">§{it.section_number} {it.section_title ?? ""}</td>
                <td className="p-2">{it.change_summary}</td>
                <td className="p-2">{it.is_material ? <Badge variant="destructive">Material</Badge> : <Badge variant="outline">Minor</Badge>}</td>
              </tr>
            ))}</tbody>
          </table>}
      </CardContent>
    </Card>
  );
}
